// HomeHeroCard — chain hero matching the prototype's ChainHero block.
//
// Real data sources:
//   - useRolloverChain()         : current_week, total_rollovers, opening_stake, current_value, status
//   - useChainProtectedSeries()  : per-week cumulative_protected_amount series
//   - useRolloverProtection()    : cumulativeProtected (running total)
//
// When no chain exists, renders the prototype's "Phantom warm-up" variant
// — copy-faithful, derived from no real money. No mock numbers are
// fabricated; phantom amounts are the prototype's static walk-through values.

import { useMemo } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { useRolloverChain } from "../../vault/useRolloverChain.ts";
import { useRolloverProtection } from "../../vault/useRolloverProtection.ts";
import { useChainProtectedSeries } from "./useChainProtectedSeries.ts";

interface WeekVM {
  n: number;
  val: number;
  sub: string;
  status: "done" | "active" | "future" | "busted";
}

const NO_CYCLE_IDS: readonly string[] = [];

function fmt(n: number): string {
  return n.toLocaleString();
}

function buildPhantomWeeks(): WeekVM[] {
  return [
    { n: 1, val: 100, sub: "Phantom start", status: "active" },
    { n: 2, val: 173, sub: "if won", status: "future" },
    { n: 3, val: 299, sub: "if won", status: "future" },
    { n: 4, val: 518, sub: "if won", status: "future" },
    { n: 5, val: 897, sub: "target", status: "future" },
  ];
}

function buildLiveWeeks(
  totalRollovers: number,
  currentWeek: number,
  openingStake: number,
  series: ReadonlyArray<{ weekNumber: number; cumulativeProtected: number }>,
  currentValue: number,
): WeekVM[] {
  const rows: WeekVM[] = [];
  const total = Math.max(totalRollovers || 5, 5);
  const seriesByWeek = new Map(series.map((p) => [p.weekNumber, p.cumulativeProtected]));
  for (let i = 1; i <= total; i++) {
    let val: number;
    if (i < currentWeek) {
      val = seriesByWeek.get(i) ?? Math.round(openingStake * Math.pow(1.732, i - 1));
    } else if (i === currentWeek) {
      val = Math.round(currentValue);
    } else {
      val = Math.round(openingStake * Math.pow(1.732, i - 1));
    }
    let status: WeekVM["status"];
    let sub: string;
    if (i < currentWeek) {
      status = "done";
      sub = "Won";
    } else if (i === currentWeek) {
      status = "active";
      sub = "In progress";
    } else if (i === total) {
      status = "future";
      sub = "Target";
    } else {
      status = "future";
      sub = "if won";
    }
    rows.push({ n: i, val, sub, status });
  }
  return rows;
}

export function HomeHeroCard(): JSX.Element {
  const { chain } = useRolloverChain();
  const { series } = useChainProtectedSeries(chain?.id ?? null);
  const cycleIds = useMemo(() => [...NO_CYCLE_IDS], []);
  const protection = useRolloverProtection(chain?.id ?? null, cycleIds);

  const isFree = chain === null;

  const currentWeek = chain?.current_week ?? 0;
  const totalWeeks = Math.max(chain?.total_rollovers ?? 5, 5);
  const openingStake = chain?.opening_stake ?? 100;
  const currentValue = chain?.current_value ?? openingStake;

  const weeks = isFree
    ? buildPhantomWeeks()
    : buildLiveWeeks(totalWeeks, currentWeek, openingStake, series, currentValue);

  const target = isFree ? 897 : Math.round(openingStake * Math.pow(1.732, totalWeeks - 1));
  const heroValue = isFree ? 100 : Math.round(currentValue);
  const protectedAmt = isFree ? 0 : (protection.cumulativeProtected ?? 0);
  const nextStake = isFree ? 100 : Math.round(currentValue);
  const completed = isFree ? 0 : Math.max(0, currentWeek - 1);

  const deltaWeeks = isFree ? 0 : Math.max(0, currentWeek - 1);
  const deltaAmount = isFree ? 0 : Math.round(currentValue - openingStake);

  return (
    <div className="chain-hero">
      <div className="chain-head">
        <div>
          <div className="label">
            {isFree ? "Phantom warm-up · 5-week ROLLOVER" : "Active chain · 5-week ROLLOVER"}
          </div>
          <div className="title">
            {isFree
              ? "Phantom warm-up — no money at risk"
              : `Goal: chain in progress · ${String(totalWeeks)}-week plan`}
          </div>
        </div>
        <div className="head-tags">
          <span className={`tag ${isFree ? "phantom" : "safe"}`}>
            <span className="dot" />
            {isFree ? "Phantom" : "Live"}
          </span>
          <span className="tag safe">
            <span className="dot" />
            Safe Mode
          </span>
          <span className="tag">Phase 1A</span>
        </div>
      </div>

      <div className="chain-body">
        <div className="chain-amount">
          <div className="row">
            <span className="currency">GH₵</span>
            <span className="big">{fmt(heroValue)}</span>
            {!isFree && deltaWeeks > 0 && (
              <span className="delta">
                <span className="arrow">↑</span> +GH₵{fmt(deltaAmount)} · {String(deltaWeeks)} week
                {deltaWeeks === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="caption">
            {isFree
              ? "Simulated value. Walk through one cycle to feel how compounding works — then turn on Live."
              : `Active stake riding into Week ${String(currentWeek)}. Compounds from previous wins.`}
          </div>
          <div className="stake-chips">
            <div className="stake-chip">
              <div className="k">Next stake</div>
              <div className="v">GH₵{fmt(nextStake)}</div>
            </div>
            <div className="stake-chip">
              <div className="k">Target value</div>
              <div className="v">GH₵{fmt(target)}</div>
            </div>
            <div className="stake-chip">
              <div className="k">Protected</div>
              <div className="v">GH₵{fmt(protectedAmt)}</div>
            </div>
          </div>
        </div>

        <div className="weeks">
          <div className="weeks-head">
            <div className="lab">{String(totalWeeks)}-week chain · weekly cadence</div>
            <div className="prog">
              {String(completed)} / {String(totalWeeks)} complete
            </div>
          </div>
          <div className="weeks-track">
            {weeks.map((w) => (
              <div
                key={w.n}
                className={`week ${w.status === "done" ? "done" : ""} ${
                  w.status === "active" ? "active" : ""
                } ${w.status === "busted" ? "busted" : ""}`}
              >
                <div className="wstatus" />
                <div className="wn">WEEK {String(w.n)}</div>
                <div className="wval">GH₵{fmt(w.val)}</div>
                <div className="wsub">{w.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="chain-foot">
        <div className="next">
          {isFree ? (
            <>
              Your <strong>Week 1 brief</strong> opens when you start the phantom walk-through.
            </>
          ) : (
            <>
              Your <strong>Week {String(currentWeek)} brief</strong> drives the next decision.
            </>
          )}
        </div>
        <div className="actions">
          <button className="home-btn ghost" type="button">
            Chain settings
          </button>
          <button className="home-btn primary" type="button">
            {isFree ? "Open the brief" : `Review Week ${String(currentWeek)} brief`}
            <Icon name="arrow" size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
